import { Injectable } from '@angular/core';
import { IAvailabilityGetData } from '@core/availability/availability-get-data.model';
import { AvailabilityService } from '@core/availability/availability.service';
import { ICalendarAvailability } from '@core/availability/calendar-availability.model';
import { DateFormatService } from '@core/date/date-format.service';
import { BehaviorSubject, firstValueFrom, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { CDatesByScreenSize, CDefaultNumDates } from './dates-by-screen-size.const';
import { IWhenAttending } from './when-attending.model';
import { IVenueConfig } from '@core/venue-config/models/venue-config.model';

@Injectable({
  providedIn: 'root'
})
export class WhenAttendingService {
  /** This is the core date when creating order items. It's set by a number of factors, and may change by guest selection in the UI of the select date */
  private _selectedDate: IWhenAttending = { date: new Date() };
  private selectedDate$: BehaviorSubject<IWhenAttending> = new BehaviorSubject<IWhenAttending>(this._selectedDate);
  private showCalendarModal$: Subject<true> = new Subject<true>();
  private isAnythingAvailable$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private numberOfDatesToDisplay$: BehaviorSubject<number> = new BehaviorSubject<number>(CDefaultNumDates);
  private checkoutStartDate: Date | undefined;

  constructor(
    private dateFormatService: DateFormatService,
    private availabilityService: AvailabilityService
  ) {}

  /**
   * Creates an array of attending dates.
   * @returns an array of When attending
   */
  createWhenAttendingArray(fromDate: Date | undefined, productId: string | undefined): Observable<IWhenAttending[]> {
    const today = new Date();
    const options: IAvailabilityGetData = {
      isShort: true,
      date: fromDate,
      productId
    };

    return this.availabilityService.getAvailabilityData(options).pipe(
      map((availability: ICalendarAvailability[]) => {
        const isAnythingAvailable = availability.some(
          (cal: ICalendarAvailability) => cal.availability !== 'notAvailable'
        );
        this.isAnythingAvailable$.next(isAnythingAvailable);

        return availability.map((value: ICalendarAvailability) => {
          return {
            date: this.dateFormatService.dateOnlyStringToDate(value.date),
            isToday: this.dateFormatService.isSameDay(today, value.date),
            availability: value.availability,
            isSelected: this.dateFormatService.isSameDay(value.date, fromDate)
          };
        });
      })
    );
  }

  getSelectedDateSync(): IWhenAttending {
    return this._selectedDate;
  }

  /** Gets the current behaviorSubject as an observable. */
  getSelectedDate(): Observable<IWhenAttending | undefined> {
    return this.selectedDate$.asObservable();
  }

  /** Sets the selected Date behaviorSubject, after ensuring there is no time element. */
  setSelectedDate(date: IWhenAttending): void {
    date.date = this.dateFormatService.getDateWithoutTime(date.date);
    this._selectedDate = date;
    this.selectedDate$.next(this._selectedDate);
  }

  /** Gets the current subject containing the isAnythingAvailable. Only emits when it has value. */
  getIsAnythingAvailable(): Observable<boolean> {
    return this.isAnythingAvailable$.asObservable();
  }

  /** Gets the current subject trigger for showing the calendar modal. Only emits when it passes a value after subscribed. */
  getShowCalendarModal(): Observable<true> {
    return this.showCalendarModal$.asObservable();
  }

  /** External trigger to showing the calendar modal inside the when attending component */
  setShowCalendarModal(): void {
    this.showCalendarModal$.next(true);
  }

  /** Get the number of dates that should be displayed in a row for given screen */
  getNumberOfDatesToDisplay(): Observable<number> {
    return this.numberOfDatesToDisplay$.asObservable();
  }

  /** Sets the number of dates to display for a given screen size - should only be initialed in the venue component. */
  setNumberOfDatesToDisplay(screenSize: string): void {
    screenSize = screenSize.toLowerCase();
    // eslint-disable-next-line no-prototype-builtins
    const numDatesDisplay = CDatesByScreenSize.hasOwnProperty(screenSize)
      ? CDatesByScreenSize[screenSize]
      : CDefaultNumDates;
    this.numberOfDatesToDisplay$.next(numDatesDisplay);
  }

  /** Checks a date in an array, fetches new array if needed */
  async getDateArrayFresh(datesList: IWhenAttending[], date: Date): Promise<IWhenAttending[]> {
    const findDateIndex = datesList.findIndex((item: IWhenAttending) =>
      this.dateFormatService.isSameDay(item.date, date)
    );
    const numberOfDatesToDisplay = await firstValueFrom(this.getNumberOfDatesToDisplay());
    if (findDateIndex < 0 || findDateIndex > numberOfDatesToDisplay - 1) {
      datesList = await firstValueFrom(this.createWhenAttendingArray(date, undefined));
    }

    return datesList;
  }

  /**
   * Get calculated selected date based on the query params - this date will only be returned and set if:
   * 1) it's set in the params
   * 2) the config allows the date to be changed
   * 3) the param date is not before the checkout start date
   * 4) the param date is a valid date and not a date in the past
   */
  getCalculatedSelectedDate(date: string | null | undefined, venueConfig: IVenueConfig): IWhenAttending {
    if (!date) {
      return this._selectedDate;
    }

    const paramDate = this.dateFormatService.dateOnlyStringToDate(date);
    if (!this.dateFormatService.isValidDate(paramDate)) {
      return this._selectedDate;
    }

    if (this.dateFormatService.isDayBeforeFirstDate(this.checkoutStartDate, paramDate)) {
      return this._selectedDate;
    }

    const timezoneOffset: number = venueConfig.venueSettings.timeZoneOffsetMinutes ?? 0;
    const currentVenueTime = this.dateFormatService.currentVenueTime(timezoneOffset);
    if (this.dateFormatService.isDayBeforeFirstDate(currentVenueTime, paramDate)) {
      return this._selectedDate;
    }

    const selectDate: IWhenAttending = { date: paramDate };
    const isChangedDate = !this.dateFormatService.isSameDay(this._selectedDate.date, selectDate.date);
    if (isChangedDate) {
      this.setSelectedDate(selectDate);
    }

    return selectDate;
  }

  /**
   * Sets the initial booking date and selected date in the UI based on venue config and time - may be replaced later by query params.
   *
   * If there is a start date defined, and it's not before the current venue date then set it as our initial start
   * This is common if the venue has a checkout for specific events in the future
   *
   * The default is the current venue time - leading to usually the selected date being TODAY for the guest
   */
  setInitialDate(config: IVenueConfig): IWhenAttending {
    const timezoneOffset: number = config.venueSettings.timeZoneOffsetMinutes ?? 0;
    const currentVenueTime = this.dateFormatService.currentVenueTime(timezoneOffset);
    const setStartDate: string | undefined = config.dateSettings.date;
    const venueStartDate = setStartDate ? this.dateFormatService.dateOnlyStringToDate(setStartDate) : undefined;

    if (
      this.dateFormatService.isValidDate(venueStartDate) &&
      !this.dateFormatService.isDayBeforeFirstDate(currentVenueTime, venueStartDate)
    ) {
      this.checkoutStartDate = venueStartDate;
    }

    this.setSelectedDate({ date: this.checkoutStartDate || currentVenueTime });

    return this._selectedDate;
  }
}
